import { spawnSync } from 'node:child_process';
import { log } from '@clack/prompts';
import { hostOpenCommand } from '@agentbox/sandbox-core';
import {
  detectEngine,
  getBoxHostPaths,
  inspectBox,
  portlessGetUrl,
  startBox,
  unpauseBox,
} from '@agentbox/sandbox-docker';
import { Command } from 'commander';
import { resolveBoxOrExit } from '../box-ref.js';
import { providerForBox } from '../provider/registry.js';
import { handleLifecycleError } from './_errors.js';

interface UrlOptions {
  print?: boolean;
  loopback?: boolean;
  ttl?: string;
}

/** Daytona's signed-URL ceiling is 24h; clamp the CLI flag to the same. */
const SIGNED_URL_TTL_MIN = 1;
const SIGNED_URL_TTL_MAX = 86400;

function parseTtlOrExit(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < SIGNED_URL_TTL_MIN || n > SIGNED_URL_TTL_MAX) {
    throw new Error(
      `--ttl must be an integer between ${String(SIGNED_URL_TTL_MIN)} and ${String(SIGNED_URL_TTL_MAX)} seconds`,
    );
  }
  return n;
}

export const urlCommand = new Command('url')
  .description(
    "Open a box's web app URL in the browser, even when no service declares `expose:` (auto-unpause/start)",
  )
  .argument(
    '[box]',
    'box ref: project index, id, id prefix, name, or container (default: the only box in this project)',
  )
  .option('--print', 'print the URL to stdout instead of launching the browser')
  .option(
    '--loopback',
    'use the 127.0.0.1 URL instead of the OrbStack .orb.local / Portless .localhost URL',
  )
  .option(
    '--ttl <seconds>',
    'cloud only: signed-URL expiry in seconds (default 3600, max 86400)',
  )
  .action(async (idOrName: string | undefined, opts: UrlOptions) => {
    try {
      const box = await resolveBoxOrExit(idOrName);
      const provider = box.provider ?? 'docker';

      let url: string;
      if (provider === 'docker') {
        const insp = await inspectBox(box.id);
        if (insp.state === 'paused') {
          log.info('box is paused; unpausing');
          await unpauseBox(box.id);
        } else if (insp.state === 'stopped') {
          log.info('box is stopped; starting');
          await startBox(box.id);
        } else if (insp.state === 'missing') {
          throw new Error(`box ${box.name} has no container; was it destroyed?`);
        }

        // Re-read after a possible start: startBox re-resolves & persists the
        // reallocated webHostPort (lifecycle.ts).
        const { record } = await getBoxHostPaths(box.id);
        if (record.webContainerPort === undefined) {
          throw new Error(
            `box ${box.name} predates the reserved web port; recreate it to use \`agentbox url\``,
          );
        }

        const engine = await detectEngine();
        if (engine === 'orbstack' && !opts.loopback) {
          // OrbStack auto-routes <container>.orb.local to the container; :80 is
          // declared (EXPOSE 80) so no port suffix is needed.
          url = `http://${record.container}.orb.local`;
        } else if (record.portlessAlias && !opts.loopback) {
          // A Portless route was registered — use the URL resolved at
          // create/start; fall back to a live `portless get` for older records.
          url = record.portlessUrl ?? (await portlessGetUrl(record.portlessAlias));
        } else {
          if (record.webHostPort === undefined) {
            throw new Error(
              `web port not resolved for box ${box.name}; is the container running? try \`agentbox inspect ${box.name}\``,
            );
          }
          url = `http://127.0.0.1:${String(record.webHostPort)}`;
        }
      } else {
        // Cloud provider: probeState + lifecycle handled by the provider;
        // URL is a signed preview URL (token embedded in the URL itself) so
        // the host browser can open it without a custom header.
        const ttl = parseTtlOrExit(opts.ttl);
        const p = await providerForBox(box);
        const state = await p.probeState(box);
        if (state === 'paused') {
          log.info('box is paused; resuming');
          await p.resume(box);
        } else if (state === 'stopped') {
          log.info('box is stopped; starting');
          await p.start(box);
        } else if (state === 'missing') {
          throw new Error(`cloud sandbox for ${box.name} is missing; was it deleted?`);
        }
        url = await p.resolveUrl(box, { kind: 'web', ttl });
      }

      if (opts.print) {
        process.stdout.write(`${url}\n`);
        return;
      }

      const opened = spawnSync(hostOpenCommand(), [url], { stdio: 'inherit' });
      if (opened.status !== 0) {
        throw new Error(`open ${url} failed (exit ${String(opened.status ?? 'n/a')})`);
      }
      process.stdout.write(`opened ${url}\n`);
    } catch (err) {
      handleLifecycleError(err);
    }
  });
